import React, { useCallback, useEffect, useRef, useState } from 'react';
import { adminAPI, squareAPI } from '../services/api';
import Icon from './Icon';

const WORKSHOP_KEYWORDS = [
  'service', 'servicing', 'repair', 'workshop', 'labour', 'labor', 'tune',
  'overhaul', 'diagnostic', 'maintenance', 'inspection', 'szerviz', 'javitas', 'muhely',
];

const BIKE_KEYWORDS = [
  'bike', 'bicycle', 'cycle', 'frame', 'chassis', 'vin', 'chain', 'cassette',
  'derailleur', 'brake', 'wheel', 'tyre', 'tire', 'fork', 'suspension', 'shimano', 'sram',
];

function smallBtnStyle(darkMode) {
  return {
    padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 500,
    background: darkMode ? 'rgba(255,255,255,0.06)' : '#f3f4f6',
    color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151',
    border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
    cursor: 'pointer',
  };
}

function containsAnyKeyword(text, keywords) {
  const blob = ` ${(text || '').toLowerCase()} `;
  return keywords.some((kw) => blob.includes(` ${kw} `) || blob.includes(`${kw}:`));
}

function shouldShowFrameLinkUI(order) {
  if (!order || order.frame_number || order.dismissed_at) return false;

  const itemText = (order.items || [])
    .flatMap((item) => [item?.name, item?.note])
    .filter(Boolean)
    .join('\n');
  const text = [order.ticket_name, itemText].filter(Boolean).join('\n');

  const hasExplicitFrameMarker = /frame\s*(number|no\.?|num|#|:)/i.test(text);
  const hasWorkshopSignal = containsAnyKeyword(text, WORKSHOP_KEYWORDS);
  const hasBikeSignal = containsAnyKeyword(text, BIKE_KEYWORDS);

  if (hasExplicitFrameMarker) return true;
  return hasWorkshopSignal && hasBikeSignal;
}

/**
 * Displays recent Square tickets (from POS or API) and lets the user
 * link a bike to each ticket. Once linked, Framebook creates
 * or updates the corresponding service record automatically.
 *
 * Square ticket = Framebook service (work order).
 * Active tickets → in_progress services. Completed/paid tickets → completed services.
 */
export default function SquareIncomingOrders({ darkMode = true }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAll, setShowAll] = useState(false);

  // Bikes for frame number search
  const [bikes, setBikes] = useState([]);
  const [bikesLoaded, setBikesLoaded] = useState(false);

  // Per-order linking state (keyed by order_id)
  const [linkingState, setLinkingState] = useState({}); // { [orderId]: { frameNumber, bikeId, saving, expanded } }
  const [fullInvoiceByOrderId, setFullInvoiceByOrderId] = useState({});
  const [dismissingOrderId, setDismissingOrderId] = useState(null);

  const [stats, setStats] = useState({ total: 0, with_frame: 0, without_frame: 0 });
  const [diagnostic, setDiagnostic] = useState(null);
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);
  const incomingVersionRef = useRef(null);

  const fetchOrders = useCallback(async ({ withReconcile = false } = {}) => {
    try {
      setLoading(true);
      setError('');
      if (withReconcile) {
        try {
          await squareAPI.reconcileIncomingOrders({ limit: 20, dismiss_frameless: true });
        } catch {
          // Non-blocking: incoming list can still load even if reconcile fails.
        }
      }
      const res = await squareAPI.listIncomingOrders(showAll);
      setOrders(res.data?.orders || []);
      setStats(res.data?.stats || { total: 0, with_frame: 0, without_frame: 0 });
      if (res.data?.version) {
        incomingVersionRef.current = res.data.version;
      }
    } catch (err) {
      if (err.response?.status !== 403) {
        setError('Failed to load Square tickets');
      }
    } finally {
      setLoading(false);
    }
  }, [showAll]);

  const checkForIncomingChanges = useCallback(async () => {
    try {
      const res = await squareAPI.getIncomingOrdersVersion();
      const nextVersion = res.data?.version || null;
      if (!incomingVersionRef.current) {
        incomingVersionRef.current = nextVersion;
        return;
      }
      if (nextVersion && nextVersion !== incomingVersionRef.current) {
        await fetchOrders();
      }
    } catch {
      // Silent fallback: periodic full refresh still runs.
    }
  }, [fetchOrders]);

  const loadBikes = async () => {
    if (bikesLoaded) return;
    try {
      const res = await adminAPI.getAllBikes();
      setBikes(res.data?.bikes || []);
      setBikesLoaded(true);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const checkIfVisible = () => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        checkForIncomingChanges();
      }
    };
    const fullRefreshIfVisible = () => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        fetchOrders();
      }
    };

    // Webhook-first: initial read is pure GET; no auto-reconcile writes from UI.
    fetchOrders();
    const versionInterval = setInterval(checkIfVisible, 60000);
    const safetyRefreshInterval = setInterval(fullRefreshIfVisible, 300000);
    const onVisibilityOrFocus = () => checkIfVisible();

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityOrFocus);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', onVisibilityOrFocus);
    }

    return () => {
      clearInterval(versionInterval);
      clearInterval(safetyRefreshInterval);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityOrFocus);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', onVisibilityOrFocus);
      }
    };
  }, [fetchOrders, checkForIncomingChanges]);

  const updateLinkField = (orderId, field, value) => {
    setLinkingState(prev => ({
      ...prev,
      [orderId]: { ...prev[orderId], [field]: value }
    }));
  };

  const handleSelectBike = (orderId, bike) => {
    setLinkingState(prev => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        frameNumber: bike.vin || '',
        bikeId: bike.id,
        bikeLabel: `${bike.brand || ''} ${bike.model || ''} (${bike.vin || 'no VIN'})`.trim(),
      }
    }));
  };

  const fetchFullInvoice = async (orderId) => {
    const cur = fullInvoiceByOrderId[orderId];
    if (cur && !cur.error) return;
    setFullInvoiceByOrderId(prev => ({ ...prev, [orderId]: { loading: true } }));
    try {
      const res = await squareAPI.getOrderDebug(orderId);
      setFullInvoiceByOrderId(prev => ({ ...prev, [orderId]: res.data }));
    } catch {
      setFullInvoiceByOrderId(prev => ({ ...prev, [orderId]: { error: 'Failed to load invoice' } }));
    }
  };

  const toggleBookingDetails = (orderId) => {
    const isOpen = Boolean(fullInvoiceByOrderId[orderId]) || Boolean(linkingState[orderId]?.expanded);
    if (isOpen) {
      setFullInvoiceByOrderId(prev => {
        const next = { ...prev };
        delete next[orderId];
        return next;
      });
      setLinkingState(prev => ({
        ...prev,
        [orderId]: {
          ...prev[orderId],
          expanded: false,
        }
      }));
      return;
    }

    loadBikes();
    fetchFullInvoice(orderId);
    setLinkingState(prev => ({
      ...prev,
      [orderId]: {
        ...prev[orderId],
        expanded: true,
        frameNumber: prev[orderId]?.frameNumber || '',
        bikeId: prev[orderId]?.bikeId || '',
      }
    }));
  };

  const handleDismiss = async (orderId, isLinked = false) => {
    try {
      setDismissingOrderId(orderId);
      setError('');
      await squareAPI.dismissOrder(orderId);
      setSuccess(isLinked ? 'Ticket removed from list.' : 'Ticket dismissed (not bike-related).');
      await fetchOrders();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to dismiss order');
    } finally {
      setDismissingOrderId(null);
    }
  };

  const handleLinkFrame = async (orderId) => {
    const state = linkingState[orderId];
    if (!state?.frameNumber?.trim()) return;

    try {
      updateLinkField(orderId, 'saving', true);
      setError('');
      const res = await squareAPI.linkFrameToOrder(orderId, {
        frame_number: state.frameNumber.trim(),
        bike_id: state.bikeId || undefined,
      });
      const msg = res.data?.finalized
        ? `Linked & service created! (Service #${res.data.service_id})`
        : `Bike linked to ticket.`;
      setSuccess(msg);
      await fetchOrders();
      // Collapse after success
      setLinkingState(prev => ({ ...prev, [orderId]: { ...prev[orderId], expanded: false, saving: false } }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to link frame number');
      updateLinkField(orderId, 'saving', false);
    }
  };

  const fetchDiagnostic = async () => {
    try {
      setDiagnosticLoading(true);
      const res = await squareAPI.getOrderStats();
      setDiagnostic(res.data);
    } catch { setDiagnostic(null); } finally { setDiagnosticLoading(false); }
  };

  const formatAmount = (cents, currency) => `${(cents / 100).toFixed(2)} ${currency}`;

  const statusBadge = (status) => {
    const colors = {
      PAID: '#16a34a', COMPLETED: '#16a34a',
      PENDING: '#d97706', OPEN: '#d97706',
      FAILED: '#ef4444', CANCELED: '#ef4444',
    };
    const c = colors[status] || '#6b7280';
    return {
      padding: '2px 8px', borderRadius: '99px', fontSize: '10px', fontWeight: 600,
      background: c + '20', color: c, border: `1px solid ${c}40`,
      display: 'inline-block',
    };
  };

  const statsBar = (
    <div style={{
      display: 'flex',
      gap: '16px',
      alignItems: 'center',
      flexWrap: 'wrap',
      fontSize: '13px',
      color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280',
    }}>
      <span><strong style={{ color: darkMode ? '#fff' : '#111827' }}>{stats.total}</strong> Square ticket{stats.total !== 1 ? 's' : ''}</span>
      <span><strong style={{ color: '#16a34a' }}>{stats.with_frame}</strong> linked to bike</span>
      <span><strong style={{ color: stats.without_frame > 0 ? '#d97706' : (darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af') }}>{stats.without_frame}</strong> unlinked</span>
      <button onClick={fetchDiagnostic} disabled={diagnosticLoading} style={smallBtnStyle(darkMode)}>
        {diagnosticLoading ? '...' : 'Diagnostic'}
      </button>
      {diagnostic && (
        <div style={{
          width: '100%',
          marginTop: '8px',
          padding: '12px',
          background: darkMode ? 'rgba(0,0,0,0.2)' : '#f9fafb',
          borderRadius: '8px',
          fontSize: '12px',
          border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e5e7eb',
        }}>
          <strong>Diagnostic ({diagnostic.company_name})</strong>
          <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            <span>Sessions: {diagnostic.square_sessions?.total ?? '-'} (frame: {diagnostic.square_sessions?.with_frame_number ?? '-'}, bike: {diagnostic.square_sessions?.with_bike_id ?? '-'}, service: {diagnostic.square_sessions?.with_service_id ?? '-'})</span>
            <span style={{ color: diagnostic.square_sessions?.paid_but_no_service > 0 ? '#ef4444' : undefined }}>
              Paid but no service: {diagnostic.square_sessions?.paid_but_no_service ?? '-'}
            </span>
            <span>Admin lists: {diagnostic.admin_lists?.services_count ?? '-'} services, {diagnostic.admin_lists?.bikes_count ?? '-'} bikes</span>
          </div>
          <button onClick={() => setDiagnostic(null)} style={{ marginTop: '8px', ...smallBtnStyle(darkMode) }}>Close</button>
        </div>
      )}
    </div>
  );

  if (!loading && orders.length === 0 && !error && !showAll) {
    return (
      <div style={{
        background: darkMode ? '#1f2937' : '#ffffff',
        color: darkMode ? '#f9fafb' : '#111827',
        padding: '16px 20px', borderRadius: '8px', marginBottom: '20px',
        border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
        boxShadow: darkMode ? 'none' : '0 1px 2px rgba(0,0,0,0.05)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          {statsBar}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => setShowAll(true)} style={smallBtnStyle(darkMode)}>Show all</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: darkMode ? '#1f2937' : '#ffffff',
      color: darkMode ? '#f9fafb' : '#111827',
      padding: '20px', borderRadius: '8px', marginBottom: '20px',
      border: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #e5e7eb',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    }}>
      {/* Header + stats */}
      <div style={{ marginBottom: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name="payment" customColor="#3b82f6" size={20} />
            Square Tickets
          </h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowAll(!showAll)}
              style={{
                ...smallBtnStyle(darkMode),
                fontWeight: 600,
                background: showAll ? 'rgba(239,68,68,0.15)' : smallBtnStyle(darkMode).background,
                color: showAll ? '#ef4444' : (darkMode ? 'rgba(255,255,255,0.7)' : '#374151'),
                border: showAll ? '1px solid rgba(239,68,68,0.3)' : (darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb'),
              }}
            >
              {showAll ? 'Hide all' : 'Show all'}
            </button>
            <button onClick={fetchOrders} disabled={loading} style={smallBtnStyle(darkMode)}>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
        <div style={{ marginTop: '10px' }}>{statsBar}</div>
      </div>

      {error && <div style={{ fontSize: '13px', color: '#ef4444', marginBottom: '10px' }}>{error}</div>}
      {success && (
        <div style={{ fontSize: '13px', color: '#16a34a', marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
          <span>{success}</span>
          <button onClick={() => setSuccess('')} style={{ background: 'none', border: 'none', color: '#16a34a', cursor: 'pointer' }}>x</button>
        </div>
      )}

      {/* Orders list - compact layout */}
      {orders.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {orders.map(order => {
            const ls = linkingState[order.order_id] || {};
            const isLinked = !!order.frame_number;
            const canLinkFrame = shouldShowFrameLinkUI(order);
            const fullInv = fullInvoiceByOrderId[order.order_id];
            const showFullInv = !!fullInv;
            const fullInvLoading = fullInv?.loading;

            return (
              <div key={order.session_id} style={{
                padding: '10px 12px', borderRadius: '8px',
                background: isLinked
                  ? (darkMode ? 'rgba(34,197,94,0.06)' : 'rgba(34,197,94,0.04)')
                  : (darkMode ? 'rgba(255,255,255,0.03)' : '#fafafa'),
                border: isLinked
                  ? `1px solid ${darkMode ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.15)'}`
                  : (darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e5e7eb'),
              }}>
                {/* Order summary row - compact */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '160px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: '13px', color: darkMode ? '#fff' : '#111827' }}>
                        {formatAmount(order.amount_cents, order.currency)}
                      </span>
                      <span style={statusBadge(order.payment_status)}>{order.payment_status}</span>
                      <span style={statusBadge(order.order_status)}>{order.order_status}</span>
                      <span style={{
                        fontSize: '9px', padding: '2px 5px', borderRadius: '4px',
                        background: order.source === 'Square POS' ? 'rgba(59,130,246,0.15)' : 'rgba(245,158,11,0.15)',
                        color: order.source === 'Square POS' ? '#3b82f6' : '#d97706',
                      }}>
                        {order.source}
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.45)' : '#9ca3af' }}>
                      {order.created_at ? new Date(order.created_at).toLocaleString() : 'Unknown date'}
                      {order.order_id && <span> · {order.order_id.slice(0, 8)}…</span>}
                    </div>
                    {order.ticket_name && (
                      <div style={{ marginTop: '2px', fontSize: '11px', fontWeight: 600, color: darkMode ? '#60a5fa' : '#2563eb', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Icon name="bike" customColor={darkMode ? '#60a5fa' : '#2563eb'} size={11} />
                        {order.ticket_name}
                      </div>
                    )}
                    {/* Line items - compact, max 2 visible by default for unlinked */}
                    {order.items && order.items.length > 0 && (
                      <div style={{ marginTop: '4px', fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.55)' : '#6b7280' }}>
                        {order.items.slice(0, isLinked ? 99 : 2).map((item, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '6px', padding: '1px 0' }}>
                            <span>
                              {item.quantity > 1 ? `${item.quantity}x ` : ''}{item.name}
                              {item.note && (
                                <span style={{ color: darkMode ? 'rgba(255,255,255,0.35)' : '#9ca3af', fontSize: '10px', marginLeft: '4px' }}>
                                  - {item.note.split('\n')[0]}
                                </span>
                              )}
                            </span>
                            <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                              {(() => {
                                const qty = item.quantity || 1;
                                const cur = item.currency === 'GBP' ? '£' : (item.currency || '') + ' ';
                                const orderTotalPounds = (order.amount_cents || 0) / 100;
                                const itemsSumPounds = (order.items || []).reduce((s, i) => s + (Number(i.total_price) || 0), 0);
                                const mismatch = itemsSumPounds > orderTotalPounds + 0.01;
                                const useBase = mismatch && item.base_price != null && item.base_price > 0;
                                const lineTotal = useBase ? (item.base_price * qty) : (item.total_price != null && item.total_price > 0 ? item.total_price : (item.unit_price > 0 ? (item.unit_price * qty) : 0));
                                return lineTotal > 0 ? `${cur}${lineTotal.toFixed(2)}` : '';
                              })()}
                            </span>
                          </div>
                        ))}
                        {!isLinked && order.items.length > 2 && !showFullInv && (
                          <div style={{ fontSize: '10px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af', marginTop: '2px' }}>
                            +{order.items.length - 2} more - click "Details" for full list
                          </div>
                        )}
                      </div>
                    )}
                    {isLinked && (
                      <div style={{ marginTop: '4px', fontSize: '11px', color: '#16a34a', fontWeight: 500 }}>
                        Linked: {order.frame_number}
                        {order.service_id && <span> · S#{order.service_id}</span>}
                      </div>
                    )}
                  </div>

                  {/* Actions - compact (unlinked: link + dismiss; linked: dismiss/remove) */}
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {!isLinked && canLinkFrame && (
                    <>
                      <button
                        onClick={() => !fullInvLoading && toggleBookingDetails(order.order_id)}
                        disabled={fullInvLoading}
                        style={{
                          padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 500,
                          background: showFullInv ? (darkMode ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.12)') : (darkMode ? 'rgba(255,255,255,0.06)' : '#f3f4f6'),
                          color: showFullInv ? '#6366f1' : (darkMode ? 'rgba(255,255,255,0.7)' : '#6b7280'),
                          border: `1px solid ${showFullInv ? 'rgba(99,102,241,0.4)' : (darkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb')}`,
                          cursor: fullInvLoading ? 'wait' : 'pointer', whiteSpace: 'nowrap', opacity: fullInvLoading ? 0.7 : 1,
                        }}
                      >
                        {fullInvLoading ? 'Loading...' : fullInv?.error ? 'Retry' : showFullInv ? 'Hide details' : 'Booking details'}
                      </button>
                    </>
                  )}
                  {!isLinked && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleDismiss(order.order_id)}
                        disabled={dismissingOrderId === order.order_id}
                        title="Not bike-related — dismiss from list"
                        className="square-incoming-dismiss-btn"
                      >
                        {dismissingOrderId === order.order_id ? '…' : 'Not bike-related / Dismiss'}
                      </button>
                    </>
                  )}
                  {isLinked && (
                    <button
                      type="button"
                      onClick={() => handleDismiss(order.order_id, true)}
                      disabled={dismissingOrderId === order.order_id}
                      title="Remove from list"
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: 500,
                        background: 'rgba(239,68,68,0.12)',
                        color: '#ef4444',
                        border: '1px solid rgba(239,68,68,0.25)',
                        cursor: dismissingOrderId === order.order_id ? 'wait' : 'pointer',
                      }}
                    >
                      {dismissingOrderId === order.order_id ? '…' : 'Delete'}
                    </button>
                  )}
                  </div>
                </div>

                {/* Full invoice from Square - only when fetched successfully */}
                {!isLinked && fullInv && !fullInv.loading && !fullInv.error && (
                  <div style={{
                    marginTop: '8px', padding: '8px 10px', borderRadius: '6px',
                    background: darkMode ? 'rgba(0,0,0,0.25)' : '#f9fafb',
                    border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e5e7eb',
                    fontSize: '10px', fontFamily: 'monospace', maxHeight: '180px', overflowY: 'auto',
                    color: darkMode ? 'rgba(255,255,255,0.75)' : '#374151',
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>Square ticket details</div>
                    {(fullInv.parsed?.line_items || fullInv.raw_order?.line_items || []).map((li, i) => (
                      <div key={i} style={{ padding: '2px 0', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f3f4f6' }}>
                        {li.quantity || 1}x {li.name || '(item)'}
                        {li.note && <span style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af' }}>{' - '}{String(li.note).slice(0, 80)}</span>}
                        {(li.total_money?.amount != null || li.base_price_money?.amount != null) && (
                          <span style={{ float: 'right' }}>£{((li.total_money?.amount ?? li.base_price_money?.amount ?? 0) / 100).toFixed(2)}</span>
                        )}
                      </div>
                    ))}
                    {fullInv.parsed?.total_money && (
                      <div style={{ marginTop: '4px', fontWeight: 600 }}>
                        Total: £{((fullInv.parsed.total_money.amount || 0) / 100).toFixed(2)} {fullInv.parsed.total_money.currency || ''}
                      </div>
                    )}
                    {fullInv.parsed?.ticket_name && <div>Ticket: {fullInv.parsed.ticket_name}</div>}
                    {fullInv.parsed?.note && <div>Note: {fullInv.parsed.note}</div>}
                  </div>
                )}

                {/* Expanded: frame number input + bike search */}
                {ls.expanded && !isLinked && canLinkFrame && (
                  <div style={{
                    marginTop: '10px', paddingTop: '10px',
                    borderTop: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e5e7eb',
                  }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 200px' }}>
                        <label style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', display: 'block', marginBottom: '4px' }}>
                          Frame number *
                        </label>
                        <input
                          type="text"
                          value={ls.frameNumber || ''}
                          onChange={(e) => updateLinkField(order.order_id, 'frameNumber', e.target.value)}
                          placeholder="Enter frame number..."
                          style={{
                            width: '100%', padding: '8px 12px', borderRadius: '8px', fontSize: '13px',
                            background: darkMode ? 'rgba(255,255,255,0.06)' : '#fff',
                            border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #d1d5db',
                            color: darkMode ? '#fff' : '#111827',
                          }}
                        />
                      </div>
                      <button
                        onClick={() => handleLinkFrame(order.order_id)}
                        disabled={!ls.frameNumber?.trim() || ls.saving}
                        style={{
                          padding: '8px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                          background: !ls.frameNumber?.trim() ? (darkMode ? 'rgba(255,255,255,0.05)' : '#e5e7eb') : 'linear-gradient(135deg, #16a34a, #15803d)',
                          color: !ls.frameNumber?.trim() ? (darkMode ? 'rgba(255,255,255,0.3)' : '#9ca3af') : '#fff',
                          border: 'none', cursor: !ls.frameNumber?.trim() || ls.saving ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {ls.saving ? 'Saving...' : 'Link & Assign'}
                      </button>
                    </div>

                    {/* Quick bike search - prioritize matches by ticket_name */}
                    {bikes.length > 0 && (() => {
                      const ticket = (order.ticket_name || '').toLowerCase().trim();
                      const frameSearch = (ls.frameNumber || '').toLowerCase().trim();
                      // Sort: matching bikes first (by brand, model, or VIN containing ticket_name)
                      const scored = bikes.map(b => {
                        let score = 0;
                        const brand = (b.brand || '').toLowerCase();
                        const model = (b.model || '').toLowerCase();
                        const vin = (b.vin || '').toLowerCase();
                        if (ticket) {
                          if (brand.includes(ticket) || ticket.includes(brand)) score += 10;
                          if (model.includes(ticket) || ticket.includes(model)) score += 8;
                          if (vin.includes(ticket)) score += 15;
                        }
                        if (frameSearch) {
                          if (vin.includes(frameSearch)) score += 20;
                          if (brand.includes(frameSearch)) score += 5;
                          if (model.includes(frameSearch)) score += 5;
                        }
                        return { bike: b, score };
                      });
                      scored.sort((a, b) => b.score - a.score);
                      const hasMatches = scored[0]?.score > 0;
                      const displayed = hasMatches ? scored.filter(s => s.score > 0).slice(0, 30) : scored.slice(0, 50);

                      return (
                        <div style={{ marginTop: '10px' }}>
                          <div style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af', marginBottom: '4px' }}>
                            {hasMatches
                              ? `Suggested bikes${ticket ? ` for "${order.ticket_name}"` : ''}:`
                              : 'Or select a bike:'}
                          </div>
                          <div style={{ maxHeight: '120px', overflowY: 'auto', borderRadius: '6px', border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb' }}>
                            {displayed.map(({ bike, score }) => (
                              <button
                                key={bike.id}
                                onClick={() => handleSelectBike(order.order_id, bike)}
                                type="button"
                                style={{
                                  width: '100%', textAlign: 'left', border: 'none', padding: '8px 10px',
                                  borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f3f4f6',
                                  background: String(ls.bikeId) === String(bike.id)
                                    ? (darkMode ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.08)')
                                    : (score > 0 ? (darkMode ? 'rgba(34,197,94,0.06)' : 'rgba(34,197,94,0.04)') : 'transparent'),
                                  color: darkMode ? '#fff' : '#111827', cursor: 'pointer', fontSize: '12px',
                                }}
                              >
                                <span style={{ fontWeight: 500 }}>{bike.brand} {bike.model}</span>
                                <span style={{ color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af', marginLeft: '8px' }}>
                                  {bike.vin || 'no VIN'}
                                </span>
                                {score > 0 && (
                                  <span style={{ marginLeft: '6px', fontSize: '10px', color: '#16a34a', fontWeight: 600 }}>MATCH</span>
                                )}
                              </button>
                            ))}
                          </div>
                          {ls.bikeLabel && (
                            <div style={{ fontSize: '11px', color: '#3b82f6', marginTop: '4px' }}>
                              Selected: {ls.bikeLabel}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
